import React, { useState, useEffect } from 'react';
import { Eye, Edit2, Trash2, Search, Download, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ViewPayment = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // totals/stats
  const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
  const todayKey = new Date().toISOString().split('T')[0];
  const todayPayments = payments
    .filter(p => p.date === todayKey)
    .reduce((sum, p) => sum + p.amount, 0);
  const now = new Date();
  const monthPayments = payments
    .filter(p => {
      const d = new Date(p.date);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    })
    .reduce((sum, p) => sum + p.amount, 0);
  const yearPayments = payments
    .filter(p => new Date(p.date).getFullYear() === now.getFullYear())
    .reduce((sum, p) => sum + p.amount, 0);

  const samplePayments = [
    {
      id: 'PMT-2024-1001',
      memberName: 'Hussein Abebe',
      FIN: '8464-3774-8284',
      amount: 1500,
      date: '2024-06-10',
      method: 'Cash',
      status: 'completed',
      notes: 'Monthly loan repayment'
    },
    {
      id: 'PMT-2024-1002',
      memberName: 'Mulugeta Bekele',
      FIN: '9201-5543-1102',
      amount: 2500,
      date: '2024-06-08',
      method: 'Mobile Money',
      status: 'completed',
      notes: 'Share contribution'
    },
    {
      id: 'PMT-2024-1003',
      memberName: 'Aisha Mohamed',
      FIN: '5566-2211-3344',
      amount: 500,
      date: '2024-06-05',
      method: 'Bank Transfer',
      status: 'pending',
      notes: 'Service fee'
    },
    {
      id: 'PMT-2024-1004',
      memberName: 'Bekele Tadesse',
      FIN: '7788-9900-1122',
      amount: 8000,
      date: '2024-06-01',
      method: 'Cash',
      status: 'completed',
      notes: 'Large share purchase'
    },
    {
      id: 'PMT-2024-1005',
      memberName: 'Chernet Alemu',
      FIN: '9900-1233-4455',
      amount: 1200,
      date: '2024-05-29',
      method: 'Mobile Money',
      status: 'completed',
      notes: 'Membership fee'
    },
    {
      id: 'PMT-2024-1006',
      memberName: 'Dagmawi Gebremedhin',
      FIN: '1122-3344-5566',
      amount: 3400,
      date: '2024-05-20',
      method: 'Bank Transfer',
      status: 'completed',
      notes: 'Loan interest'
    },
    {
      id: 'PMT-2024-1007',
      memberName: 'Elimira Kassa',
      FIN: '2233-4455-6677',
      amount: 950,
      date: '2024-05-15',
      method: 'Cash',
      status: 'completed',
      notes: 'Market stall rent'
    },
    {
      id: 'PMT-2024-1008',
      memberName: 'Fikadu Shiferaw',
      FIN: '3344-5566-7788',
      amount: 2100,
      date: '2024-05-10',
      method: 'Mobile Money',
      status: 'pending',
      notes: 'Partial payment'
    }
  ];

  useEffect(() => {
    const t = setTimeout(() => {
      setPayments(samplePayments);
      setLoading(false);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  // export to csv
  const exportToCSV = () => {
    try {
      const headers = ['ID','Member','FIN','Amount','Date','Method','Status','Notes'];
      const rows = payments.map(p => [
        p.id,
        `"${p.memberName}"`,
        p.FIN,
        `${p.amount.toFixed(2)} BIRR`,
        p.date,
        p.method,
        p.status,
        `"${p.notes || ''}"`
      ].join(','));
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payments_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('export error', err);
    }
  };

  const filtered = payments.filter(p =>
    p.memberName.toLowerCase().includes(search.toLowerCase()) ||
    p.id.toLowerCase().includes(search.toLowerCase()) ||
    (p.FIN || '').toLowerCase().includes(search.toLowerCase())
  );

  const formatCurrency = (amt) => {
    try {
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amt || 0);
      return `${formatted} BIRR`;
    } catch (e) {
      return (amt || 0) + ' BIRR';
    }
  };

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return d;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 ml-8 lg:ml-24">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
            <p className="text-sm text-gray-500">View and manage payments (ETB)</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/addpayment')} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg">
              <Plus size={16}/> Add Payment
            </button>
            <button onClick={exportToCSV} className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg"><Download size={16}/> Export</button>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
            <p className="text-gray-500 text-sm">Today</p>
            <h3 className="text-xl font-bold text-gray-800">{formatCurrency(todayPayments)}</h3>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
            <p className="text-gray-500 text-sm">This Month</p>
            <h3 className="text-xl font-bold text-gray-800">{formatCurrency(monthPayments)}</h3>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
            <p className="text-gray-500 text-sm">This Year</p>
            <h3 className="text-xl font-bold text-gray-800">{formatCurrency(yearPayments)}</h3>
          </div>
        </div>
+        <div className="bg-white rounded-xl shadow border border-gray-100 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by member, ID or FIN" className="w-full pl-10 pr-4 py-2 border rounded-lg" />
            {/* total amount display */}
            <div className="mt-2 text-right text-sm text-gray-600">Total: <span className="font-bold text-gray-800">{formatCurrency(totalPayments)}</span></div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="min-w-full divide-y text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Payment ID</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Member</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">FIN</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Amount</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Date</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Method</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Status</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan="8" className="px-6 py-8 text-center text-gray-400">Loading payments...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-8 text-center text-gray-400">No payments found.</td></tr>
              ) : (
                filtered.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-mono text-sm text-blue-500">{p.id}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-blue-500">{p.memberName}</div>
                    </td>
                    <td className="px-6 py-4 font-mono">{p.FIN}</td>
                    <td className="px-6 py-4 text-blue-500">{formatCurrency(p.amount)}</td>
                    <td className="px-6 py-4 text-blue-500">{formatDate(p.date)}</td>
                    <td className="px-6 py-4">{p.method}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${p.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{p.status}</span>
                    </td>
                    <td className="px-6 py-4 flex gap-2">
                      <button className="text-blue-600 p-2 rounded hover:bg-blue-50"><Eye size={16}/></button>
                      <button className="text-green-600 p-2 rounded hover:bg-green-50"><Edit2 size={16}/></button>
                      <button className="text-red-600 p-2 rounded hover:bg-red-50"><Trash2 size={16}/></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ViewPayment;
